/**
 * Conversation Service - API client for conversation management
 * Provides functions for working with conversations and messages
 */
import axios from "axios";
import { tokenManager } from "../utils/tokenManager";
import { withAcceptLanguageHeader } from "../utils/requestLanguage";
import { getRequestScopeHeaders } from "../utils/requestScope";
import {
  isKeycloakAuthenticated,
  updateKeycloakToken,
  getKeycloakToken,
} from "./keycloak";

const RAG_API_URL =
  window.env.REACT_APP_RAG_API_URL || "http://localhost:8002/api/v1";

// Create axios instance for Conversation API (using same RAG API endpoint)
export const conversationApi = axios.create({
  baseURL: RAG_API_URL,
});

// Setup secure interceptors using centralized token manager
tokenManager.setupInterceptors(conversationApi);

/**
 * Helper function to handle API errors consistently
 * @param {Error} error - The error object from axios
 * @returns {Promise} Rejected promise with formatted error
 */
const handleApiError = (error) => {
  console.error("Conversation API Error:", error);

  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    const errorMessage =
      error.response.data?.detail ||
      error.response.data?.message ||
      `HTTP error ${error.response.status}`;
    return Promise.reject(new Error(errorMessage));
  } else if (error.request) {
    // The request was made but no response was received
    return Promise.reject(
      new Error("Network error - please check your connection")
    );
  } else {
    // Something happened in setting up the request that triggered an Error
    return Promise.reject(new Error(error.message || "Unknown error occurred"));
  }
};

// Conversations API
const conversationsApi = {
  /**
   * Create a new conversation
   * @param {Object} conversationData - Conversation data
   * @param {string} conversationData.name - Conversation name
   * @param {Object} conversationData.metadata - Optional conversation metadata
   * @param {string} conversationData.llm_config_id - Optional LLM configuration ID
   * @param {number} conversationData.temperature - Optional temperature setting
   * @param {number} conversationData.max_tokens - Optional max tokens setting
   * @returns {Promise<Object>} Created conversation data
   */
  createConversation: async (conversationData) => {
    try {
      const response = await conversationApi.post(
        "/conversations/",
        conversationData
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get all conversations
   * @param {Object} options - Query options
   * @param {number} options.skip - Number of records to skip (pagination)
   * @param {number} options.limit - Number of records to return (pagination)
   * @param {boolean} options.include_archived - Whether to include archived conversations
   * @param {string} options.search - Optional search term
   * @returns {Promise<Array>} List of conversations
   */
  getConversations: async ({
    skip = 0,
    limit = 50,
    include_archived = false,
    search = "",
  } = {}) => {
    try {
      const params = new URLSearchParams();
      if (skip) params.append("skip", skip);
      if (limit) params.append("limit", limit);
      if (include_archived) params.append("include_archived", include_archived);
      if (search) params.append("search", search);

      const response = await conversationApi.get(
        `/conversations/?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get a specific conversation with messages
   * @param {string} conversationId - Conversation UUID
   * @returns {Promise<Object>} Conversation data with messages
   */
  getConversation: async (conversationId) => {
    try {
      const response = await conversationApi.get(
        `/conversations/${conversationId}`
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Update an existing conversation
   * @param {string} conversationId - Conversation UUID
   * @param {Object} conversationData - Conversation data to update
   * @param {string} conversationData.name - Updated conversation name
   * @param {Object} conversationData.metadata - Updated conversation metadata
   * @param {number} conversationData.temperature - Updated temperature setting
   * @param {number} conversationData.max_tokens - Updated max tokens setting
   * @param {boolean} conversationData.is_archived - Updated archived status
   * @returns {Promise<Object>} Updated conversation data
   */
  updateConversation: async (conversationId, conversationData) => {
    try {
      const response = await conversationApi.put(
        `/conversations/${conversationId}`,
        conversationData
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Delete a conversation
   * @param {string} conversationId - Conversation UUID
   * @param {boolean} hard_delete - Whether to permanently delete the conversation
   * @returns {Promise<void>}
   */
  deleteConversation: async (conversationId, hard_delete = false) => {
    try {
      const params = new URLSearchParams();
      if (hard_delete) params.append("hard_delete", hard_delete);

      await conversationApi.delete(
        `/conversations/${conversationId}?${params.toString()}`
      );
      return true;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Archive or unarchive a conversation
   * @param {string} conversationId - Conversation UUID
   * @param {boolean} archive - Whether to archive (true) or unarchive (false)
   * @returns {Promise<Object>} Updated conversation data
   */
  archiveConversation: async (conversationId, archive = true) => {
    try {
      const response = await conversationApi.patch(
        `/conversations/${conversationId}/archive`,
        {
          archive,
        }
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Bulk delete conversations
   * @param {Array<string>} conversationIds - Array of conversation UUIDs
   * @returns {Promise<Object>} Result with deleted count
   */
  bulkDeleteConversations: async (conversationIds) => {
    try {
      const response = await conversationApi.post(
        "/conversations/bulk/delete",
        {
          conversation_ids: conversationIds,
        }
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Bulk archive conversations
   * @param {Array<string>} conversationIds - Array of conversation UUIDs
   * @param {boolean} archive - Whether to archive (true) or unarchive (false)
   * @returns {Promise<Object>} Result with updated count
   */
  bulkArchiveConversations: async (conversationIds, archive = true) => {
    try {
      const response = await conversationApi.post(
        "/conversations/bulk/archive",
        {
          conversation_ids: conversationIds,
          archive,
        }
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Transcribe audio file for a conversation
   * @param {string} conversationId - Conversation UUID
   * @param {File} audioFile - Audio file to transcribe
   * @param {string} language - Optional language code (e.g., 'en', 'ar')
   * @returns {Promise<Object>} Transcription result
   */
  transcribeAudio: async (conversationId, audioFile, language = null) => {
    try {
      const formData = new FormData();
      formData.append("audio_file", audioFile);
      if (language) {
        formData.append("language", language);
      }

      const response = await conversationApi.post(
        `/conversations/${conversationId}/audio`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
};

// Messages API
const messagesApi = {
  /**
   * Add a message to a conversation
   * @param {string} conversationId - Conversation UUID
   * @param {Object} messageData - Message data
   * @param {string} messageData.role - Message role ("user" | "assistant" | "system")
   * @param {string|Array} messageData.content - Message content (string or multimodal array)
   * @param {Object} messageData.metadata - Optional message metadata
   * @param {Array} messageData.display_files - Optional display files for the message
   * @param {string} messageData.status - Optional message status
   * @returns {Promise<Object>} Created message data
   */
  addMessage: async (conversationId, messageData) => {
    try {
      const response = await conversationApi.post(
        `/conversations/${conversationId}/messages`,
        messageData
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get messages from a conversation
   * @param {string} conversationId - Conversation UUID
   * @param {Object} options - Query options
   * @param {number} options.skip - Number of records to skip (pagination)
   * @param {number} options.limit - Number of records to return (pagination)
   * @returns {Promise<Array>} List of messages
   */
  getMessages: async (conversationId, { skip = 0, limit = 100 } = {}) => {
    try {
      const params = new URLSearchParams();
      if (skip) params.append("skip", skip);
      if (limit) params.append("limit", limit);

      const response = await conversationApi.get(
        `/conversations/${conversationId}/messages?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Update a message
   * @param {string} messageId - Message UUID
   * @param {Object} messageData - Message data to update
   * @param {string} messageData.content - Updated message content
   * @param {Object} messageData.metadata - Updated message metadata
   * @param {string} messageData.status - Updated message status
   * @returns {Promise<Object>} Updated message data
   */
  updateMessage: async (messageId, messageData) => {
    try {
      const response = await conversationApi.put(
        `/conversations/messages/${messageId}`,
        messageData
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Delete a message
   * @param {string} messageId - Message UUID
   * @returns {Promise<void>}
   */
  deleteMessage: async (messageId) => {
    try {
      await conversationApi.delete(`/conversations/messages/${messageId}`);
      return true;
    } catch (error) {
      return handleApiError(error);
    }
  },
};

// Chat Interface API
const chatApi = {
  /**
   * Send message and get response (unified chat interface)
   * @param {string} conversationId - Conversation UUID
   * @param {Object} chatData - Chat data
   * @param {Array} chatData.messages - Array of message objects
   * @param {string} chatData.llm_config_id - Optional LLM configuration ID
   * @param {number} chatData.temperature - Optional temperature setting
   * @param {number} chatData.max_tokens - Optional max tokens setting
   * @param {Object} chatData.metadata - Optional metadata (e.g., collection_id for RAG)
   * @returns {Promise<Object>} Chat response
   */
  sendMessage: async (conversationId, chatData) => {
    try {
      // Determine endpoint based on whether collection is selected
      const isRagChat = chatData.metadata && chatData.metadata.collection_id;
      const endpoint = isRagChat
        ? `/conversations/${conversationId}/chat/rag`
        : `/conversations/${conversationId}/chat`;

      // Using RAG or regular chat endpoint

      const response = await conversationApi.post(endpoint, chatData);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Send message and stream response (unified chat interface)
   * @param {string} conversationId - Conversation UUID
   * @param {Object} chatData - Chat data
   * @param {Array} chatData.messages - Array of message objects
   * @param {string} chatData.llm_config_id - Optional LLM configuration ID
   * @param {number} chatData.temperature - Optional temperature setting
   * @param {number} chatData.max_tokens - Optional max tokens setting
   * @param {Object} chatData.metadata - Optional metadata (e.g., collection_id for RAG)
   * @param {Function} onMessage - Callback for each message chunk or event
   * @param {Function} onComplete - Callback when stream is complete
   * @param {Function} onError - Callback when error occurs
   * @returns {AbortController} Controller to abort the stream
   */
  streamMessage: (conversationId, chatData, onMessage, onComplete, onError) => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchStream = async () => {
      try {
        // Get auth token
        await tokenManager.refreshIfExpired();
        let authToken = tokenManager.getToken();

        // Determine endpoint based on whether collection is selected
        const isRagChat = chatData.metadata && chatData.metadata.collection_id;
        const endpoint = isRagChat
          ? `${RAG_API_URL}/conversations/${conversationId}/chat/rag/stream`
          : `${RAG_API_URL}/conversations/${conversationId}/chat/stream`;

        // Using RAG or regular chat endpoint

        // Make streaming fetch request
        const response = await fetch(endpoint, {
          method: "POST",
          headers: withAcceptLanguageHeader({
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            ...getRequestScopeHeaders(),
          }),
          body: JSON.stringify(chatData),
          signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `HTTP error ${response.status}`);
        }

        // Process the SSE stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          // Process complete lines
          const lines = buffer.split("\n");
          buffer = lines.pop() || ""; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const data = line.slice(6);
              if (data === "[DONE]") {
                if (onComplete) onComplete();
                return;
              }

              try {
                const parsed = JSON.parse(data);
                if (onMessage) onMessage(parsed);
                if (parsed.event === "error") {
                  if (onError) onError(parsed);
                }
              } catch (e) {
                // Skip invalid JSON
              }
            }
          }
        }
      } catch (error) {
        if (error.name === "AbortError") {
          // Stream aborted by user
        } else {
          // Stream error
          if (onError) onError(error);
        }
      }
    };

    // Start the stream immediately
    fetchStream();

    // Return controller so caller can abort if needed
    return controller;
  },

  /**
   * Send message and stream response using agentic RAG
   * @param {string} conversationId - Conversation UUID
   * @param {Object} chatData - Chat data
   * @param {Array} chatData.messages - Array of message objects
   * @param {string} chatData.llm_config_id - Optional LLM configuration ID
   * @param {number} chatData.temperature - Optional temperature setting
   * @param {number} chatData.max_tokens - Optional max tokens setting
   * @param {Object} chatData.metadata - Required metadata for agentic RAG
   * @param {string} chatData.metadata.collection_id - Collection UUID (required)
   * @param {boolean} chatData.metadata.use_agents - Should be true for agentic mode
   * @param {Object} chatData.metadata.agent_config - Agent configuration
   * @param {Object} chatData.metadata.retrieval_config - Retrieval configuration
   * @param {Function} onMessage - Callback for each message chunk or event
   * @param {Function} onComplete - Callback when stream is complete
   * @param {Function} onError - Callback when error occurs
   * @returns {AbortController} Controller to abort the stream
   */
  streamAgentMessage: (
    conversationId,
    chatData,
    onMessage,
    onComplete,
    onError
  ) => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchStream = async () => {
      try {
        // Get auth token
        await tokenManager.refreshIfExpired();
        let authToken = tokenManager.getToken();

        if (!authToken) {
          throw new Error("User is not authenticated");
        }

        // Use agentic RAG endpoint
        const endpoint = `${RAG_API_URL}/conversations/${conversationId}/chat/agents/stream`;

        // Make streaming fetch request
        const response = await fetch(endpoint, {
          method: "POST",
          headers: withAcceptLanguageHeader({
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            ...getRequestScopeHeaders(),
          }),
          body: JSON.stringify(chatData),
          signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `HTTP error ${response.status}`);
        }

        // Process the SSE stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          // Process complete lines
          const lines = buffer.split("\n");
          buffer = lines.pop() || ""; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const data = line.slice(6);
              if (data === "[DONE]") {
                if (onComplete) onComplete();
                return;
              }

              try {
                const parsed = JSON.parse(data);
                if (onMessage) onMessage(parsed);
              } catch (e) {
                // Skip invalid JSON
              }
            }
          }
        }
      } catch (error) {
        if (error.name === "AbortError") {
          // Stream aborted by user
        } else {
          // Stream error
          if (onError) onError(error);
        }
      }
    };

    // Start the stream immediately
    fetchStream();

    // Return controller so caller can abort if needed
    return controller;
  },
};

// Search API
const searchApi = {
  /**
   * Search messages across conversations
   * @param {Object} options - Search options
   * @param {string} options.q - Search query
   * @param {number} options.limit - Number of results to return
   * @param {number} options.skip - Number of results to skip
   * @returns {Promise<Array>} Search results with message and conversation data
   */
  searchMessages: async ({ q, limit = 10, skip = 0 } = {}) => {
    try {
      const params = new URLSearchParams();
      if (q) params.append("q", q);
      if (limit) params.append("limit", limit);
      if (skip) params.append("skip", skip);

      const response = await conversationApi.get(
        `/conversations/search/messages?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get conversation statistics
   * @returns {Promise<Object>} Statistics data
   */
  getStats: async () => {
    try {
      const response = await conversationApi.get("/conversations/stats");
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
};

// Export all APIs individually
export { conversationsApi, messagesApi, chatApi, searchApi };

// Export as a unified service
const conversationService = {
  conversations: conversationsApi,
  messages: messagesApi,
  chat: chatApi,
  search: searchApi,
};

export default conversationService;
